"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.exactStringMatch = exactStringMatch;
exports.insertModelContent = insertModelContent;
exports.updateModelContent = updateModelContent;
exports.getSearchContent = getSearchContent;
exports.getLocalizedContent = getLocalizedContent;
exports.getDefaultLocaleFromContent = getDefaultLocaleFromContent;
exports.hasLocaleTranslation = hasLocaleTranslation;
exports.slimMediaForIndex = slimMediaForIndex;
const logger_1 = require("../logger");
/**
 * Builds a Builder.io query matcher for an exact match against a STRING field,
 * resilient to Builder's numeric coercion.
 *
 * Builder coerces an unquoted, numeric-looking query value to a number, so a
 * purely-numeric value (e.g. a clinical-trial slug "300383" or orgStudyId
 * "20240178") never matches the value stored as a string — `{ field: "300383" }`
 * and `{ field: { $eq: "300383" } }` both return nothing. An anchored regex
 * forces string comparison and matches exactly. Regex metacharacters in the
 * value are escaped so the match stays exact.
 */
function exactStringMatch(value) {
    const escaped = value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    return { $regex: `^${escaped}$` };
}
async function insertModelContent(content, model, apiKey) {
    const METHOD = content.id ? "PATCH" : "POST";
    logger_1.logger.debug("Inserting content:", "builderUtils", content);
    logger_1.logger.debug("Model:", "builderUtils", model);
    logger_1.logger.debug("API Key:", "builderUtils", apiKey);
    const response = await fetch(`https://builder.io/api/v1/write/${model}`, {
        method: "POST",
        headers: {
            Authorization: `Bearer ${apiKey}`,
            "Content-Type": "application/json",
        },
        body: JSON.stringify(content),
    });
    if (!response.ok) {
        const error = await response.json();
        logger_1.logger.error("Failed to write to Builder.io:", "builderUtils", error);
        throw new Error(`Failed to write to Builder.io: ${error.message || response.statusText}`);
    }
    return response.json();
}
async function updateModelContent(content, model, id, apiKey) {
    const endpoint = `https://builder.io/api/v1/write/${model}/${id}`;
    logger_1.logger.info("Executing endpoint", endpoint);
    const response = await fetch(endpoint, {
        method: "PATCH",
        headers: {
            Authorization: `Bearer ${apiKey}`,
            "Content-Type": "application/json",
        },
        body: JSON.stringify(content),
    });
    if (!response.ok) {
        const error = await response.json();
        logger_1.logger.error("Failed to write to Builder.io:", "builderUtils", error);
        throw new Error(`Failed to write to Builder.io: ${error.message || response.statusText}`);
    }
    return response.json();
}
// Helper function to strip HTML tags
const stripHtml = (html) => {
    return html.replace(/<[^>]*>/g, "");
};
/**
 * Basic recursive function to extract text content from an object
 */
const extractTextFromObject = (obj, locale) => {
    const results = [];
    if (obj === null || obj === undefined) {
        return results;
    }
    // Handle LocalizedValue objects
    if (typeof obj === "object" && obj["@type"] === "@builder.io/core:LocalizedValue") {
        const localizedValue = obj;
        const value = localizedValue[locale] || localizedValue.Default || "";
        if (typeof value === "string" && value.trim()) {
            results.push(stripHtml(value));
        }
        return results;
    }
    // Handle arrays
    if (Array.isArray(obj)) {
        for (const item of obj) {
            results.push(...extractTextFromObject(item, locale));
        }
        return results;
    }
    // Handle objects
    if (typeof obj === "object") {
        for (const [key, value] of Object.entries(obj)) {
            // Focus on common content fields
            if (key === "title" || key === "subtitle" || key === "text" || key === "content" || key === "description" || key === "body") {
                results.push(...extractTextFromObject(value, locale));
            }
            else if (key === "blocks" && Array.isArray(value)) {
                // Handle Builder.io blocks structure
                results.push(...extractTextFromObject(value, locale));
            }
            else if (typeof value === "object") {
                results.push(...extractTextFromObject(value, locale));
            }
        }
        return results;
    }
    // Handle strings
    if (typeof obj === "string" && obj.trim()) {
        results.push(stripHtml(obj));
    }
    return results;
};
// Algolia's 10 KB per-record limit leaves roughly 5 KB for searchContent after
// other record fields. Capping here prevents oversized records from being
// silently rejected by Algolia's ingestion pipeline.
const SEARCH_CONTENT_MAX_CHARS = 5000;
/**
 * Gets the searchable content from a BuilderContent object for a specific locale
 * by extracting values from translatable fields and stripping HTML tags.
 * Total character count across all returned strings is capped at 5,000 to keep
 * Algolia records under the 10 KB per-record limit.
 */
function getSearchContent(content, locale) {
    const all = extractTextFromObject(content, locale).filter(Boolean);
    const result = [];
    let totalChars = 0;
    for (const s of all) {
        if (totalChars >= SEARCH_CONTENT_MAX_CHARS)
            break;
        const remaining = SEARCH_CONTENT_MAX_CHARS - totalChars;
        if (s.length > remaining) {
            result.push(s.slice(0, remaining));
            break;
        }
        result.push(s);
        totalChars += s.length;
    }
    return result;
}
/**
 * Gets a localized version of a BuilderContent object by traversing it and
 * resolving all LocalizedValue objects to their locale-specific strings
 */
function getLocalizedContent(content, locale) {
    const localize = (obj) => {
        if (obj === null || obj === undefined) {
            return obj;
        }
        // Check if it's a LocalizedValue object
        if (typeof obj === "object" &&
            obj["@type"] === "@builder.io/core:LocalizedValue") {
            const localizedValue = obj;
            return localizedValue[locale] || localizedValue.Default || "";
        }
        // If it's an array, process each element
        if (Array.isArray(obj)) {
            return obj.map((item) => localize(item));
        }
        // If it's an object, process each property
        if (typeof obj === "object") {
            const result = {};
            for (const [key, value] of Object.entries(obj)) {
                result[key] = localize(value);
            }
            return result;
        }
        // Return primitive values as-is
        return obj;
    };
    // Create a deep clone and localize it
    return localize(JSON.parse(JSON.stringify(content)));
}
/**
 * Determines the default locale for content based on its regionalCareSites.
 * If regionalCareSites contains only 'dch', default locale is 'es'.
 * Otherwise, default locale is 'en'.
 */
function getDefaultLocaleFromContent(content) {
    const regionalCareSites = content.data?.regionalCareSites;
    if (Array.isArray(regionalCareSites) && regionalCareSites.length === 1 && regionalCareSites[0] === 'dch') {
        return 'es';
    }
    return 'en';
}
/**
 * Checks if content has actual translations for a specific locale
 * (not just fallback to Default). Returns true only if at least one
 * LocalizedValue field explicitly has the requested locale.
 *
 * Content with no LocalizedValue fields anywhere is not localized at all —
 * models written by an importer (clinical trials, providers) store plain
 * values. That content is treated as a translation for the default locale
 * only, so it still gets indexed as English-only content instead of being
 * skipped for every locale.
 *
 * @param defaultLocale - The site's default locale (e.g., 'en' or 'es').
 *   If provided, "Default" in LocalizedValue is treated as a translation for this locale.
 */
function hasLocaleTranslation(content, locale, defaultLocale) {
    let foundAnyLocalizedValue = false;
    const checkForLocale = (obj) => {
        if (obj === null || obj === undefined)
            return false;
        if (typeof obj === "object" &&
            obj["@type"] === "@builder.io/core:LocalizedValue") {
            foundAnyLocalizedValue = true;
            // Check for explicit locale match
            if (locale in obj && obj[locale] !== undefined && obj[locale] !== null) {
                return true;
            }
            // If this locale is the default and "Default" exists, treat it as a translation
            if (locale === defaultLocale && "Default" in obj && obj.Default !== undefined && obj.Default !== null) {
                return true;
            }
            return false;
        }
        if (Array.isArray(obj)) {
            return obj.some((item) => checkForLocale(item));
        }
        if (typeof obj === "object") {
            return Object.values(obj).some((value) => checkForLocale(value));
        }
        return false;
    };
    const hasTranslation = checkForLocale(content);
    if (!foundAnyLocalizedValue) {
        return locale === (defaultLocale ?? "en");
    }
    return hasTranslation;
}
/**
 * Strips a Builder.io media object down to only the fields needed for Algolia
 * search results. Removes the full Bynder `thumbnails` map (20+ size variants),
 * asset metadata, and other data not consumed by search UI components, reducing
 * each Algolia record by ~10-15 KB.
 *
 * Components read: additionalInfo.selectedFile.url, assets[0].files["1200x800"],
 * assets[0].files["640x426"], assets[0].derivatives.
 */
function slimMediaForIndex(media) {
    if (!media || typeof media !== "object")
        return media;
    const m = media;
    const additionalInfo = m.additionalInfo;
    const slimAdditionalInfo = additionalInfo
        ? { selectedFile: additionalInfo.selectedFile ?? undefined }
        : undefined;
    const rawAssets = m.assets;
    const assetArray = Array.isArray(rawAssets)
        ? rawAssets
        : rawAssets != null
            ? [rawAssets]
            : [];
    const slimAssets = assetArray.slice(0, 1).map((asset) => {
        if (!asset || typeof asset !== "object")
            return asset;
        const a = asset;
        const files = a.files;
        return {
            ...(files?.["1200x800"] ? { "1200x800": files["1200x800"] } : {}),
            ...(files?.["640x426"] ? { "640x426": files["640x426"] } : {}),
            derivatives: a.derivatives ?? undefined,
        };
    });
    return {
        ...(slimAdditionalInfo !== undefined ? { additionalInfo: slimAdditionalInfo } : {}),
        ...(slimAssets.length > 0 ? { assets: slimAssets } : {}),
    };
}
